import { OpenNewTabOutlineIcon } from '@ikyucom/design-system-icons-react'
import TransparentAccordion from '~/components/accordion/sd/TransparentAccordion'
import { getFragmentData, graphql } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import { useIsPC } from '~/lib/ua'
import { css, sva } from '~/styled-system/css'
import { vstack } from '~/styled-system/patterns'
import { Button } from '~/ui'

const RestaurantAnnouncement = graphql(/* GraphQL */ `
  fragment RestaurantAnnouncement on Restaurant {
    announcement {
      title
      content
      url
    }
  }
`)

type Props = {
  restaurant: FragmentType<typeof RestaurantAnnouncement>
}

const styles = sva({
  slots: ['root', 'heading', 'announcementTitle', 'announcementContent'],
  base: {
    root: vstack.raw({
      gap: '2',
      alignItems: 'start',
      p: '3',
      w: 'full',
      _pc: {
        gap: '5',
        p: '0',
      },
    }),
    heading: {
      fontSize: 'lg',
      fontWeight: 'bold',
      _pc: {
        fontSize: '3xl',
      },
    },
    announcementTitle: {
      fontSize: 'sm',
      fontWeight: 'bold',
    },
    announcementContent: {
      fontSize: 'sm',
      lineHeight: 'normal',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
    },
  },
})

export function Announcement({ restaurant }: Props) {
  const { announcement } = getFragmentData(RestaurantAnnouncement, restaurant)
  const isPc = useIsPC()

  if (!announcement) {
    return null
  }

  const classes = styles()
  return (
    <section className={classes.root}>
      <h3 className={classes.heading}>{isPc ? 'お知らせ' : '店舗からのお知らせ'}</h3>
      <TransparentAccordion>
        <div
          className={vstack({
            width: 'full',
            gap: '1',
            alignItems: 'start',
            background: 'gray.100',
            borderRadius: 'default',
            p: '3',
            md: {
              p: '6',
            },
          })}
        >
          <div className={vstack({ gap: '1', alignItems: 'start' })}>
            <h2 className={classes.announcementTitle}>{announcement.title}</h2>
            <p className={classes.announcementContent}>{announcement.content}</p>
          </div>
          {announcement.url && (
            <Button
              asChild
              size="xs"
              css={{
                background: 'white',
                border: 'medium',
              }}
              rightIcon={
                <OpenNewTabOutlineIcon
                  width={12}
                  height={12}
                  className={css({ fill: 'gray.800' })}
                />
              }
            >
              <a href={announcement.url} target="_blank" rel="noreferrer">
                詳細をみる
              </a>
            </Button>
          )}
        </div>
      </TransparentAccordion>
    </section>
  )
}
